<script setup lang="ts">
import { type Category, CategoryType } from "mws-ts-v2-sdk";

import useCategoryFilters from "~/composables/useCategoryFilters";
import useCategoryTabs from "~/composables/useCategoryTabs";
import {
  eventsOverviewPageSizes,
  useEventsOverviewInfiniteQuery,
} from "~/queries/catalog/events";
import { CategoryTabs } from "~/types/Category";

const {
  category,
  showHistoricalEvents = true,
  enableAutoscroll = false,
  relatedCategories,
} = defineProps<{
  category: Category;
  showHistoricalEvents?: boolean;
  enableAutoscroll?: boolean;
  description?: string;
  relatedCategories?: Category[];
}>();
const scrollOffset = 24;

const { tabId, tabs } = useCategoryTabs(
  () => category,
  () => showHistoricalEvents,
);

const {
  enabled: filtersEnabled,
  filters,
  sort,
  clearFilters,
} = useCategoryFilters(() => category, tabId);

watch(filters, () => {
  scrollToStaticHeader();
});

const onUpdateTab = (newTab: CategoryTabs) => {
  tabId.value = newTab;
  sort.clear();
  clearFilters();
  scrollToStaticHeader(scrollOffset);
};

const showEventsInCardView = computed(
  () => tabId.value === CategoryTabs.Upcoming || category.showAsCardView,
);

const {
  data: eventsWithProducts,
  isPending: eventsWithProductsIsPending,
  isFetching: eventsWithproductsIsFetching,
  isSuccess: eventsWithproductsIsSuccess,
  isError: eventsWithproductsIsError,
  hasNextPage: eventsWithproductsHasNextPage,
  fetchNextPage: eventsWithproductsFetchNextPage,
} = useEventsOverviewInfiniteQuery({
  pageSize: eventsOverviewPageSizes.category,
  liveStatus: tabId,
  // When we show the events as cards we don't need any products
  numberOfProducts: () => (showEventsInCardView.value ? 0 : 25),
  categoryId: () => category.id,
  filters,
  sort,
});

// we show the match scored balls block if the category is a league,
// the category is not filtered and the current tab is 'active'.
const showMatchScoredBalls = computed(() => {
  return (
    toValue(category)?.externalReference?.type === CategoryType.League &&
    filters.value.length === 0 &&
    tabId.value === CategoryTabs.Live
  );
});

const sportCategory = computed(() =>
  relatedCategories?.find(
    (c) => c.externalReference?.type === CategoryType.Sport,
  ),
);

const isFootballTeamPage = computed(
  () =>
    sportCategory.value?.name === "Football" &&
    category.externalReference?.type === CategoryType.Team,
);
</script>

<template>
  <div class="block-container category-block md:mt-4">
    <BaseTabs
      :model-value="tabId"
      :tabs="tabs"
      @update:model-value="onUpdateTab"
    >
      <template #default>
        <LazyCategoryMatchScoredBallsSection
          v-if="showMatchScoredBalls"
          :category="category"
        />

        <EventList
          class="py-6"
          :data="eventsWithProducts"
          :is-loading="eventsWithProductsIsPending"
          :is-fetching="eventsWithproductsIsFetching"
          :is-success="eventsWithproductsIsSuccess"
          :is-error="eventsWithproductsIsError"
          :has-next-page="eventsWithproductsHasNextPage"
          :enable-autoscroll="enableAutoscroll"
          :enable-no-events-found-message="true"
          :show-in-card-view="showEventsInCardView"
          tracking-source="categoryEvents"
          @load-more="eventsWithproductsFetchNextPage"
        />
        <slot />
        <CategoryDescription
          v-if="description"
          class="pt-10"
          :description="description"
          :name="category.name"
        />
        <CategoryStaticContent
          v-if="isFootballTeamPage"
          :club-name="category.name"
        />
      </template>
      <template #actions="{ active }">
        <div
          v-if="filtersEnabled"
          class="category-events__actions-container"
        >
          <LazyCategoryEventFilters
            :category-slug="category.slug"
            :active-filters="filters"
            :active-tab="active"
          />
        </div>
      </template>
    </BaseTabs>
  </div>
</template>
